import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { api, getToken, setToken } from './lib/api';
import { Brand, Topbar } from './components/chrome';
import {
  IconApps,
  IconGear,
  IconGrid,
  IconQr,
  IconRocket,
  IconScroll,
  IconShield,
  IconTablet,
  IconBell,
} from './components/icons';

interface Admin {
  id: string;
  email: string;
  fullName: string;
  role: string;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'checking' | 'authenticated' | 'anonymous'>(
    getToken() ? 'checking' : 'anonymous',
  );

  useEffect(() => {
    if (!getToken()) { setState('anonymous'); return; }
    let cancelled = false;
    api
      .get<{ user: Admin }>('/auth/me')
      .then(() => { if (!cancelled) setState('authenticated'); })
      .catch(() => { if (!cancelled) { setToken(null); setState('anonymous'); } });
    return () => { cancelled = true; };
  }, []);

  if (state === 'checking') return <div className="empty">Checking your session…</div>;
  if (state === 'anonymous') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const NAV = [
  { to: '/', label: 'Dashboard', icon: IconGrid, end: true },
  { to: '/devices', label: 'Devices', icon: IconTablet },
  { to: '/enrollment', label: 'Enrollment', icon: IconQr },
  { to: '/applications', label: 'Applications', icon: IconApps },
  { to: '/deployments', label: 'Deployments', icon: IconRocket },
  { to: '/notifications', label: 'Notifications', icon: IconBell },
  { to: '/policies', label: 'Policies', icon: IconShield },
  { to: '/audit-logs', label: 'Audit log', icon: IconScroll },
  { to: '/settings', label: 'Settings', icon: IconGear },
];

export function Shell() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState<Admin | null>(null);

  useEffect(() => {
    const load = () => {
      api.get<{ user: Admin }>('/auth/me').then((r) => setAdmin(r.user)).catch(() => undefined);
    };
    load();
    window.addEventListener('tab-admin-updated', load);
    return () => window.removeEventListener('tab-admin-updated', load);
  }, []);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* audited server-side; ignore */ }
    setToken(null);
    navigate('/login', { replace: true });
  };

  return (
    <div className="app">
      <aside className="sidebar">
        <Brand />
        <nav>
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              <item.icon />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="spacer" />
        <div className="footer">
          <button className="small ghost" onClick={logout}>Sign out</button>
        </div>
      </aside>
      <div className="workspace">
        <Topbar adminName={admin?.fullName ?? 'Administrator'} role={admin?.role ?? ''} />
        <main className="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
